import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit as firestoreLimit,
  type QueryConstraint,
  Timestamp,
  updateDoc,
  increment
} from 'firebase/firestore'
import { db } from '@/config/firebase'
import type { ChallengeCategory } from '@/types/challenge'
import { CategoryRepository } from './CategoryRepository'

// Optimized interfaces matching the new schema
interface Challenge {
  id: string
  slug: string
  categoryId: string
  icon: string
  difficulty: 1 | 2 | 3 | 4 | 5
  estimatedDuration: number
  defaultFrequency: {
    mode: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom'
    interval?: number
    daysOfWeek?: number[]
    daysOfMonth?: number[]
    times?: string[]
    startHour?: number
    endHour?: number
    timezone?: string
  }
  tags: string[]
  prerequisites?: string[]
  order: number
  status: 'active' | 'archived'
  metadata: {
    createdAt: Date
    updatedAt: Date
    createdBy: string
    totalUsers: number
    averageRating?: number
    totalRatings?: number
  }
}

interface ChallengeTranslation {
  id: string
  challengeId: string
  language: string
  name: string
  description: string
  shortDescription: string
  instructions?: string[]
  tips?: string[]
  benefits?: string[]
  motivation?: {
    dailyQuotes?: string[]
    milestoneMessages?: string[]
  }
}

interface ChallengeFilters {
  categoryId?: string
  difficulty?: number[]
  tags?: string[]
  status?: string
  limit?: number
  orderBy?: 'popularity' | 'difficulty' | 'name' | 'newest'
}

/**
 * Challenge Repository for scalable database queries
 * 
 * This repository implements the schema with separate base entities
 * and translation documents for optimal performance at scale.
 */
export class ChallengeRepository {
  private translationCache = new Map<string, Map<string, ChallengeTranslation>>()
  private challengeCache = new Map<string, Challenge>()
  private categoryRepository = new CategoryRepository()

  /**
   * Get challenges with translations in a single optimized query
   */
  async getChallenges(language: string = 'en', filters: ChallengeFilters = {}): Promise<Challenge[]> {
    try {
      // Build base query for challenges
      const constraints: QueryConstraint[] = [
        where('status', '==', filters.status || 'active')
      ]

      if (filters.categoryId) {
        constraints.push(where('categoryId', '==', filters.categoryId))
      }

      if (filters.difficulty?.length) {
        constraints.push(where('difficulty', 'in', filters.difficulty))
      }

      if (filters.tags?.length) {
        // Use array-contains-any for efficient tag filtering
        constraints.push(where('tags', 'array-contains-any', filters.tags))
      }

      // Add ordering
      if (filters.orderBy === 'popularity') {
        constraints.push(orderBy('metadata.totalUsers', 'desc'))
      } else if (filters.orderBy === 'difficulty') {
        constraints.push(orderBy('difficulty', 'asc'))
      } else if (filters.orderBy === 'newest') {
        constraints.push(orderBy('metadata.createdAt', 'desc'))
      } else {
        constraints.push(orderBy('order', 'asc'))
      }

      if (filters.limit) {
        constraints.push(firestoreLimit(filters.limit))
      }

      // Execute optimized parallel queries
      const [challengesSnapshot, translationsSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'challenges'), ...constraints)),
        getDocs(query(
          collection(db, 'challengeTranslations'),
          where('language', '==', language)
        ))
      ])

      // Build translation lookup map
      const translationMap = new Map<string, ChallengeTranslation>()
      translationsSnapshot.docs.forEach(doc => {
        const translation = { id: doc.id, ...doc.data() } as ChallengeTranslation
        translationMap.set(translation.challengeId, translation)
      })

      // Combine challenges with translations
      const combinedChallenges: Challenge[] = challengesSnapshot.docs
        .map(doc => {
          const challenge = { id: doc.id, ...doc.data() } as Challenge
          const translation = translationMap.get(challenge.id)

          if (!translation) {
            console.warn(`Missing translation for challenge ${challenge.id} in language ${language}`)
            return null
          }

          return this.combineData(challenge, translation)
        })
        .filter((challenge): challenge is Challenge => challenge !== null)

      // Cache results
      this.updateCache(challengesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Challenge)))
      this.updateTranslationCache(language, translationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ChallengeTranslation)))

      return combinedChallenges

    } catch (error) {
      console.error('Failed to get challenges:', error)
      throw error
    }
  }

  /**
   * Get a single challenge by ID with translation
   */
  async getChallenge(id: string, language: string = 'en'): Promise<Challenge | null> {
    try {
      // Check cache first
      const cachedChallenge = this.challengeCache.get(id)
      const cachedTranslation = this.translationCache.get(language)?.get(id)

      if (cachedChallenge && cachedTranslation) {
        return this.combineData(cachedChallenge, cachedTranslation)
      }

      // Fetch from database
      const [challengeDoc, translationDoc] = await Promise.all([
        getDoc(doc(db, 'challenges', id)),
        getDoc(doc(db, 'challengeTranslations', `${id}_${language}`))
      ])

      if (!challengeDoc.exists()) {
        return null
      }

      const challenge = { id: challengeDoc.id, ...challengeDoc.data() } as Challenge

      if (!translationDoc.exists()) {
        console.warn(`Missing translation for challenge ${id} in language ${language}`)
        // Try falling back to English
        if (language !== 'en') {
          return this.getChallenge(id, 'en')
        }
        return null
      }

      const translation = { id: translationDoc.id, ...translationDoc.data() } as ChallengeTranslation

      // Update cache
      this.challengeCache.set(id, challenge)
      if (!this.translationCache.has(language)) {
        this.translationCache.set(language, new Map())
      }
      this.translationCache.get(language)!.set(id, translation)

      return this.combineData(challenge, translation)

    } catch (error) {
      console.error(`Failed to get challenge ${id}:`, error)
      throw error
    }
  }

  /**
   * Search challenges efficiently with text search on translations
   */
  async searchChallenges(searchTerm: string, language: string = 'en', filters: ChallengeFilters = {}): Promise<Challenge[]> {
    try {
      // For text search, we need to get all translations first and filter in memory
      // This is more efficient than multiple queries for small result sets
      const translationsSnapshot = await getDocs(query(
        collection(db, 'challengeTranslations'),
        where('language', '==', language)
      ))

      // Filter translations by search term
      const matchingTranslations = translationsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as ChallengeTranslation))
        .filter(translation => 
          translation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          translation.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          translation.shortDescription.toLowerCase().includes(searchTerm.toLowerCase())
        )

      if (matchingTranslations.length === 0) {
        return []
      }

      // Get challenge IDs that match the search
      const challengeIds = matchingTranslations.map(t => t.challengeId)

      // Now get the base challenges with filters
      const constraints: QueryConstraint[] = [
        where('status', '==', filters.status || 'active')
      ]

      if (filters.categoryId) {
        constraints.push(where('categoryId', '==', filters.categoryId))
      }

      // Get challenges and filter by IDs
      const challengesSnapshot = await getDocs(query(collection(db, 'challenges'), ...constraints))
      const challenges = challengesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Challenge))
        .filter(challenge => challengeIds.includes(challenge.id))

      // Combine with translations
      const translationMap = new Map(matchingTranslations.map(t => [t.challengeId, t]))
      
      return challenges
        .map(challenge => {
          const translation = translationMap.get(challenge.id)
          return translation ? this.combineData(challenge, translation) : null
        })
        .filter((challenge): challenge is Challenge => challenge !== null)
        .slice(0, filters.limit || 50) // Apply limit after filtering

    } catch (error) {
      console.error('Failed to search challenges:', error)
      throw error
    }
  }

  /**
   * Get challenges by category efficiently
   */
  async getChallengesByCategory(categoryId: string, language: string = 'en', limit: number = 20): Promise<Challenge[]> {
    return this.getChallenges(language, { 
      categoryId, 
      limit,
      orderBy: 'popularity'
    })
  }

  /**
   * Get popular challenges across all categories
   */
  async getPopularChallenges(language: string = 'en', limit: number = 10): Promise<Challenge[]> {
    return this.getChallenges(language, {
      limit,
      orderBy: 'popularity'
    })
  }

  /**
   * Get challenges by difficulty level
   */
  async getChallengesByDifficulty(difficulty: number[], language: string = 'en', limit: number = 20): Promise<Challenge[]> {
    return this.getChallenges(language, {
      difficulty,
      limit,
      orderBy: 'difficulty'
    })
  }

  /**
   * Batch load challenges by IDs (for user dashboard)
   */
  async getChallengesByIds(ids: string[], language: string = 'en'): Promise<Challenge[]> {
    if (ids.length === 0) return []

    try {
      // Batch requests in chunks of 10 (Firestore limit for 'in' queries)
      const chunks = this.chunkArray(ids, 10)
      const allChallenges: Challenge[] = []
      
      for (const chunk of chunks) {
        const challengesSnapshot = await getDocs(query(
          collection(db, 'challenges'),
          where('__name__', 'in', chunk)
        ))
        allChallenges.push(...challengesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Challenge)))
      }

      // Get translations for these challenges
      const translationIds = ids.map(id => `${id}_${language}`)
      const translationChunks = this.chunkArray(translationIds, 10)
      const allTranslations: ChallengeTranslation[] = []

      for (const chunk of translationChunks) {
        const translationsSnapshot = await getDocs(query(
          collection(db, 'challengeTranslations'),
          where('__name__', 'in', chunk)
        ))
        allTranslations.push(...translationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ChallengeTranslation)))
      }

      // Combine data
      const translationMap = new Map(allTranslations.map(t => [t.challengeId, t]))
      
      return allChallenges
        .map(challenge => {
          const translation = translationMap.get(challenge.id)
          return translation ? this.combineData(challenge, translation) : null
        })
        .filter((challenge): challenge is Challenge => challenge !== null)

    } catch (error) {
      console.error('Failed to get challenges by IDs:', error)
      throw error
    }
  }

  /**
   * Get available languages for a challenge
   */
  async getAvailableLanguages(challengeId: string): Promise<string[]> {
    try {
      const translationsSnapshot = await getDocs(query(
        collection(db, 'challengeTranslations'),
        where('challengeId', '==', challengeId)
      ))

      return translationsSnapshot.docs.map(doc => doc.data().language)
    } catch (error) {
      console.error(`Failed to get available languages for challenge ${challengeId}:`, error)
      return []
    }
  }

  /**
   * Private helper methods
   */
  private combineData(challenge: Challenge, translation: ChallengeTranslation): Challenge {
    return {
      id: challenge.id,
      name: translation.name,
      description: translation.description,
      instructions: translation.instructions || [],
      tips: translation.tips || [],
      benefits: translation.benefits || [],
      icon: challenge.icon,
      category: challenge.categoryId,
      defaultFrequency: challenge.defaultFrequency,
      order: challenge.order,
      status: challenge.status,
      language: translation.language,
      participantsCount: challenge.metadata.totalUsers || 0,
      createdAt: this.convertTimestamp(challenge.metadata.createdAt),
      updatedAt: this.convertTimestamp(challenge.metadata.updatedAt)
    }
  }

  private convertTimestamp(timestamp: any): string | undefined {
    if (!timestamp) return undefined
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate().toISOString()
    }
    if (timestamp instanceof Date) {
      return timestamp.toISOString()
    }
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toISOString()
    }
    if (timestamp.seconds && timestamp.nanoseconds !== undefined) {
      return new Timestamp(timestamp.seconds, timestamp.nanoseconds).toDate().toISOString()
    }
    return undefined
  }

  private updateCache(challenges: Challenge[]) {
    challenges.forEach(challenge => {
      this.challengeCache.set(challenge.id, challenge)
    })
  }

  private updateTranslationCache(language: string, translations: ChallengeTranslation[]) {
    if (!this.translationCache.has(language)) {
      this.translationCache.set(language, new Map())
    }
    const languageCache = this.translationCache.get(language)!
    translations.forEach(translation => {
      languageCache.set(translation.challengeId, translation)
    })
  }

  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = []
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size))
    }
    return chunks
  }

  /**
   * Get categories (delegate to CategoryRepository)
   */
  async getCategories(language: string = 'en'): Promise<ChallengeCategory[]> {
    const categoryChallenges = await this.categoryRepository.getCategories(language)
    
    // Convert CategoryChallenge to ChallengeCategory
    return categoryChallenges.map(category => ({
      id: category.id,
      name: category.name,
      description: category.description,
      icon: category.icon,
      language: category.language,
      status: category.status as 'active' | 'archived',
      order: category.order,
      createdAt: category.createdAt instanceof Date ? category.createdAt.toISOString() : category.createdAt,
      updatedAt: category.updatedAt instanceof Date ? category.updatedAt.toISOString() : category.updatedAt
    }))
  }

  /**
   * Get category (delegate to CategoryRepository)
   */
  async getCategory(id: string, language: string = 'en'): Promise<ChallengeCategory | null> {
    const categoryChallenge = await this.categoryRepository.getCategory(id, language)
    
    if (!categoryChallenge) {
      return null
    }
    
    // Convert CategoryChallenge to ChallengeCategory
    return {
      id: categoryChallenge.id,
      name: categoryChallenge.name,
      description: categoryChallenge.description,
      icon: categoryChallenge.icon,
      language: categoryChallenge.language,
      status: categoryChallenge.status as 'active' | 'archived',
      order: categoryChallenge.order,
      createdAt: categoryChallenge.createdAt instanceof Date ? categoryChallenge.createdAt.toISOString() : categoryChallenge.createdAt,
      updatedAt: categoryChallenge.updatedAt instanceof Date ? categoryChallenge.updatedAt.toISOString() : categoryChallenge.updatedAt
    }
  }

  /**
   * Increment participant count for a challenge
   */
  async incrementParticipantCount(challengeId: string): Promise<void> {
    try {
      const challengeRef = doc(db, 'challenges', challengeId)
      await updateDoc(challengeRef, {
        'metadata.totalUsers': increment(1),
        'metadata.updatedAt': Timestamp.now()
      })
      
      // Clear cache to ensure fresh data
      this.challengeCache.delete(challengeId)
    } catch (error) {
      // Users don't have permission to update challenge counts
      // This could be handled server-side with Cloud Functions
      // For now, we'll silently fail as it's not critical
    }
  }

  /**
   * Decrement participant count for a challenge
   */
  async decrementParticipantCount(challengeId: string): Promise<void> {
    try {
      const challengeRef = doc(db, 'challenges', challengeId)
      
      // Get current count to ensure we don't go below 0
      const challengeDoc = await getDoc(challengeRef)
      if (challengeDoc.exists()) {
        const currentCount = challengeDoc.data().metadata?.totalUsers || 0
        const newCount = Math.max(0, currentCount - 1)
        
        await updateDoc(challengeRef, {
          'metadata.totalUsers': newCount,
          'metadata.updatedAt': Timestamp.now()
        })
        
        // Clear cache to ensure fresh data
        this.challengeCache.delete(challengeId)
      }
    } catch (error) {
      console.error('Failed to decrement participant count:', error)
      throw error
    }
  }

  /**
   * Get participant count for a challenge
   */
  async getParticipantCount(challengeId: string): Promise<number> {
    try {
      const challengeRef = doc(db, 'challenges', challengeId)
      const challengeDoc = await getDoc(challengeRef)
      
      if (challengeDoc.exists()) {
        return challengeDoc.data().metadata?.totalUsers || 0
      }
      
      return 0
    } catch (error) {
      console.error('Failed to get participant count:', error)
      return 0
    }
  }

  /**
   * Clear cache (useful for testing or after updates)
   */
  clearCache() {
    this.challengeCache.clear()
    this.translationCache.clear()
    this.categoryRepository.clearCache()
  }
}